import type { PlaylistTheme } from 'types/playlist'
import api from '@disco/api/server'
import { createAppSlice } from '@disco/utils/slices'
import { PayloadAction, SerializedError } from '@reduxjs/toolkit'

export type State = {
  isFetching: boolean
  isFetched: boolean
  error: any // TODO: switch to "error: SerializedError | null | undefined" after migration
  themes: Array<PlaylistTheme>
}
const initialState: State = {
  isFetching: false,
  isFetched: false,
  error: null,
  themes: [],
}

const rejectedHandler = (
  state: State,
  action: PayloadAction<unknown, string, any, SerializedError>,
) => {
  state.isFetching = false
  state.error = action.error
}

const pendingHandler = (state: State) => {
  state.isFetching = true
}

const themesSlice = createAppSlice({
  name: 'themes',
  initialState,
  reducers: create => ({
    loadThemes: create.asyncThunk(
      async () => {
        return api.getThemes()
      },
      {
        pending: (state: State) => {
          state.isFetching = true
          state.themes = []
        },
        fulfilled: (state: State, action) => {
          state.isFetching = false
          state.isFetched = true
          state.error = null
          state.themes = action.payload.themes ?? []
        },
        rejected: rejectedHandler,
      },
    ),
    deleteTheme: create.asyncThunk(
      async (theme: PlaylistTheme) => {
        await api.deleteTheme(theme)
        return theme
      },
      {
        pending: pendingHandler,
        fulfilled: (state: State, { payload: theme }) => {
          state.isFetching = false
          state.error = null
          const deletedItemIndex = state.themes.findIndex(t => {
            return t.url === theme.url
          })
          state.themes.splice(deletedItemIndex, 1)
        },
        rejected: rejectedHandler,
      },
    ),
    updateTheme: create.asyncThunk(
      async ({
        savedTheme,
        theme,
        onProgress,
      }: {
        savedTheme: PlaylistTheme
        theme: PlaylistTheme
        onProgress: ((arg0: number) => void) | null | undefined
      }) => {
        const result = await api.updateTheme(savedTheme, theme, onProgress)
        return result
      },
      {
        pending: pendingHandler,
        fulfilled: (
          state: State,
          {
            payload: theme,
          }: {
            payload: PlaylistTheme
          },
        ) => {
          const { themes } = state
          const updatedThemeIndex = themes.findIndex(t => {
            return t.url === theme.url
          })
          let updatedThemes = themes

          if (theme.default) {
            updatedThemes = themes.map((t, index) => {
              if (index === updatedThemeIndex) return theme
              return { ...t, default: false }
            })
          } else {
            updatedThemes[updatedThemeIndex] = theme
          }

          state.isFetching = false
          state.error = null
          state.themes = updatedThemes
        },
        rejected: rejectedHandler,
      },
    ),
    createTheme: create.asyncThunk(
      async ({
        theme,
        onProgress,
      }: {
        theme: PlaylistTheme
        onProgress: (arg0: number) => void
      }) => {
        const result = await api.createTheme(theme, onProgress)
        return result
      },
      {
        pending: pendingHandler,
        fulfilled: (
          state: State,
          {
            payload: theme,
          }: {
            payload: PlaylistTheme
          },
        ) => {
          const { themes } = state
          let updatedThemes = themes

          if (theme.default) {
            updatedThemes = [
              ...themes.map(t => ({ ...t, default: false })),
              theme,
            ]
          } else {
            updatedThemes.push(theme)
          }

          state.isFetching = false
          state.error = null
          state.themes = updatedThemes
        },
        rejected: rejectedHandler,
      },
    ),
  }),
})

// issues with flowgen
export const loadThemes: any = themesSlice.actions.loadThemes
export const createTheme: any = themesSlice.actions.createTheme
export const deleteTheme: any = themesSlice.actions.deleteTheme
export const updateTheme: any = themesSlice.actions.updateTheme

export const themesReducer: (State, any) => State = themesSlice.reducer
